import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Eye, Timer, Trophy, RotateCcw, CheckCircle } from 'lucide-react';

const PatternGame = () => {
  const { t } = useTranslation();
  const [pattern, setPattern] = useState([]);
  const [userPattern, setUserPattern] = useState([]);
  const [isShowingPattern, setIsShowingPattern] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const colors = ['neon-blue', 'neon-green', 'neon-pink', 'neon-yellow', 'neon-purple'];

  const generatePattern = useCallback(() => {
    const patternLength = Math.min(3 + level, 8);
    const newPattern = [];
    
    for (let i = 0; i < patternLength; i++) {
      newPattern.push(colors[Math.floor(Math.random() * colors.length)]);
    }
    
    setPattern(newPattern);
    setUserPattern([]);
    setScore(0);
    setTime(0);
    setIsPlaying(false);
    setIsCompleted(false);
  }, [level]);

  useEffect(() => {
    generatePattern();
  }, [generatePattern]);

  useEffect(() => {
    let interval;
    if (isPlaying && !isCompleted) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, isCompleted]);

  const startGame = () => {
    setIsPlaying(true);
    showPattern();
  };

  const showPattern = () => {
    setIsShowingPattern(true);
    let index = 0;
    
    const interval = setInterval(() => {
      if (index < pattern.length) {
        // Highlight the current color
        setUserPattern([pattern[index]]);
        index++;
      } else {
        clearInterval(interval);
        setIsShowingPattern(false);
        setUserPattern([]);
      }
    }, 800);
  };

  const handleColorClick = (color) => {
    if (isShowingPattern || !isPlaying) return;
    
    const newUserPattern = [...userPattern, color];
    setUserPattern(newUserPattern);
    
    // Check if the pattern is correct so far
    const isCorrect = newUserPattern.every((c, i) => c === pattern[i]);
    
    if (!isCorrect) {
      // Wrong pattern
      setScore(prev => Math.max(0, prev - 50));
      setTimeout(() => {
        setUserPattern([]);
      }, 1000);
    } else if (newUserPattern.length === pattern.length) {
      // Pattern completed correctly
      const timeBonus = Math.max(0, 200 - Math.floor(time / 10) * 10);
      const newScore = 100 + timeBonus;
      setScore(prev => prev + newScore);
      
      setTimeout(() => {
        if (level < 5) {
          setLevel(prev => prev + 1);
        } else {
          setIsCompleted(true);
          setIsPlaying(false);
        }
      }, 1500);
    }
  };

  const resetGame = () => {
    generatePattern();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getColorClasses = (color) => {
    const colorMap = {
      'neon-blue': 'bg-neon-blue/20 border-neon-blue text-neon-blue',
      'neon-green': 'bg-neon-green/20 border-neon-green text-neon-green',
      'neon-pink': 'bg-neon-pink/20 border-neon-pink text-neon-pink',
      'neon-yellow': 'bg-neon-yellow/20 border-neon-yellow text-neon-yellow',
      'neon-purple': 'bg-neon-purple/20 border-neon-purple text-neon-purple'
    };
    return colorMap[color] || colorMap['neon-blue'];
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Eye className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !isCompleted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Pattern Master Challenge
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Watch the pattern sequence carefully and then repeat it by clicking the colors in the same order.
            The pattern gets longer with each level!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Completion Message */}
      {isCompleted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <CheckCircle className="h-16 w-16 text-neon-green mx-auto mb-4" />
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            All Levels Completed!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Total Time: {formatTime(time)}
          </p>
          <button
            onClick={resetGame}
            className="cyber-button text-lg px-6 py-3"
          >
            Play Again
          </button>
        </motion.div>
      )}

      {/* Pattern Display */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <h4 className="text-xl font-cyber font-bold text-white mb-6 text-center">
            {isShowingPattern ? 'Watch the Pattern' : 'Repeat the Pattern'}
          </h4>
          
          {/* Pattern Sequence */}
          <div className="flex justify-center space-x-4 mb-8">
            {pattern.map((color, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`w-16 h-16 rounded-lg border-2 flex items-center justify-center text-2xl font-bold ${
                  getColorClasses(color)
                }`}
              >
                {isShowingPattern && userPattern.includes(color) ? '✨' : '●'}
              </motion.div>
            ))}
          </div>

          {/* User Progress */}
          <div className="text-center mb-6">
            <p className="text-gray-300 font-tech mb-2">
              Your Progress: {userPattern.length} / {pattern.length}
            </p>
            <div className="w-full bg-dark-100 rounded-full h-3 border border-neon-blue/30">
              <motion.div
                className="bg-gradient-to-r from-neon-blue to-neon-green h-full rounded-full transition-all duration-500"
                initial={{ width: 0 }}
                animate={{ width: `${(userPattern.length / pattern.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Color Buttons */}
          <div className="grid grid-cols-5 gap-4 max-w-md mx-auto">
            {colors.map((color) => (
              <motion.button
                key={color}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleColorClick(color)}
                disabled={isShowingPattern || !isPlaying}
                className={`w-16 h-16 rounded-lg border-2 font-bold text-2xl transition-all duration-300 ${
                  getColorClasses(color)
                } ${isShowingPattern || !isPlaying ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'}`}
              >
                ●
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Game Stats */}
      {isPlaying && (
        <div className="cyber-card p-6">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Game Statistics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 font-tech">Pattern Length</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{pattern.length}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Current Progress</p>
                <p className="text-2xl font-cyber font-bold text-neon-green">
                  {userPattern.length > 0 ? Math.round((userPattern.length / pattern.length) * 100) : 0}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Time Bonus</p>
                <p className="text-2xl font-cyber font-bold text-neon-pink">
                  {Math.max(0, 200 - Math.floor(time / 10) * 10)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatternGame;
